<template>
	<AppBottomSheet :active="active" :force-width="bottomSheetWidth" @toggling-popup="open => $emit('toggling-popup', open)">
		<div v-if="active">
			<ModalHeader
				:title="getString('manual_self_invoice_title')"
				:info-button="getString('manual_self_invoice_explain')"
				:button-text="getString('manual_invoice_emit')"
				:button-disabled="!isFormValid"
				:button-loading="emitting"
				@click-button="emitSelfInvoice" />
			<div class="self-invoice-form" :class="{wide: isWideForm}">
				<p class="self-invoice-section-title">{{ getString('self_invoice_supplier') }}</p>
				<div class="form-grid">
					<AppTextInput :label="getString('self_invoice_supplier_name')" :value="supplier.supplier_name" @changing-text="v => supplier.supplier_name = v" />
					<div>
						<AppTextInput :label="getString('vat')" :value="supplier.supplier_vat" @changing-text="v => supplier.supplier_vat = v" />
						<p v-if="supplier.supplier_country_code && !isEuCountry" class="form-hint">{{ getString('self_invoice_extra_eu_vat_hint') }}</p>
					</div>
					<AppTextInput
						class="country-input"
						readonly
						:label="getString('country')"
						:value="supplier.supplier_country_code ? countryName(supplier.supplier_country_code) : ''"
						:placeholder="getString('select_country')"
						@click-input="countriesOpen = true" />
					<!-- Keyed on the country so the autocomplete restriction follows it -->
					<AppGoogleMapsInput
						v-if="supplier.supplier_country_code"
						:key="supplier.supplier_country_code"
						:label="getString('legal_address')"
						:placeholder="getString('address')"
						:value="formattedAddress"
						:country-restriction="supplier.supplier_country_code"
						no-map
						@changed-address="setAddress" />
				</div>

				<p class="self-invoice-section-title">{{ getString('manual_self_invoice_reference') }}</p>
				<div class="form-grid">
					<AppTextInput :label="getString('manual_self_invoice_reference_numero')" :value="reference.numero" @changing-text="v => reference.numero = v" />
					<AppTextInput type="date" :label="getString('manual_self_invoice_reference_date')" :value="reference.date" @changing-text="v => reference.date = v" />
				</div>

				<p class="self-invoice-section-title">{{ getString('manual_invoice_lines') }}</p>
				<ManualInvoiceLines v-model="lines" :vat-rates="vatRates" :price-label="getString('manual_self_invoice_unit_price')" />

				<div class="totals">
					<p>{{ getString('taxable_amount') }} <span>{{ currencyStore.readableCurrencyValue(taxable) }}</span></p>
					<p>{{ getString('vat_amount') }} <span>{{ currencyStore.readableCurrencyValue(vat) }}</span></p>
					<p class="total">{{ getString('total') }} <span>{{ currencyStore.readableCurrencyValue(taxable + vat) }}</span></p>
				</div>
			</div>
		</div>
	</AppBottomSheet>

	<CompanyCountriesBottomsheet :active="countriesOpen" @selected-country="selectCountry" @toggling-popup="v => countriesOpen = v" />
</template>

<script>
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import AppGoogleMapsInput from "@/shared/components/AppGoogleMapsInput.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import CompanyCountriesBottomsheet from "@/console-new/components/shared/CompanyCountriesBottomsheet.vue";
import ManualInvoiceLines, {emptyLine, isoDate, isValidQuantity, INVALID_DOCUMENT_ERRORS} from "@/console-new/components/config/administration/ManualInvoiceLines.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import euCountriesFile from "@/shared/assets/json/euCountrycodes.json";
import nonEuCountriesFile from "@/shared/assets/json/nonEuCountrycodes.json";
import API from "@/shared/helpers/API";
import {useUserStore} from "@/console-new/stores";
import {useCurrencyStore, useLanguageStore, useNotificationStore} from "@/shared/stores";

// Foreign suppliers without a VAT number, as SDI expects them
const EXTRA_EU_VAT = "99999999999";
const VAT_RATES = [22, 10, 5, 4];

/**
 * A TD17 self invoice for services bought from a foreign supplier and paid by other means than the cards: Italian
 * VAT is added on top of the taxable amount of the supplier invoice
 */
export default {
	setup() {
		const userStore = useUserStore();
		const languageStore = useLanguageStore();
		const currencyStore = useCurrencyStore();
		const notificationStore = useNotificationStore();
		return {userStore, currencyStore, getString: languageStore.getString, showNotification: notificationStore.showNotification};
	},
	name: "ManualSelfInvoiceBottomSheet",
	components: {AppBottomSheet, AppGoogleMapsInput, AppTextInput, ModalHeader, CompanyCountriesBottomsheet, ManualInvoiceLines},
	mixins: [WindowWidth],
	emits: ["toggling-popup", "emitted"],
	props: {
		active: {type: Boolean, default: false},
	},
	data() {
		return {
			supplier: {
				supplier_name: "",
				supplier_vat: "",
				supplier_address: "",
				supplier_civic: "",
				supplier_zip: "",
				supplier_city: "",
				supplier_country_code: "",
			},
			reference: {numero: "", date: isoDate(new Date())},
			lines: [emptyLine(VAT_RATES[0])],
			vatRates: VAT_RATES,
			countriesOpen: false,
			emitting: false,
		};
	},
	computed: {
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else {
				return "760px";
			}
		},
		isWideForm() {
			return this.windowWidth >= 768;
		},
		isEuCountry() {
			return Object.prototype.hasOwnProperty.call(euCountriesFile, this.supplier.supplier_country_code);
		},
		formattedAddress() {
			const s = this.supplier;
			if(!s.supplier_address) {
				return "";
			}
			const street = [s.supplier_address, s.supplier_civic].filter(Boolean).join(" ");
			const city = [s.supplier_zip, s.supplier_city].filter(Boolean).join(" ");
			return [street, city].filter(Boolean).join(", ");
		},
		taxable() {
			return Math.round(this.lines.reduce((total, line) => total + Math.round(line.price * 100) * line.quantity, 0));
		},
		vat() {
			return Math.round(this.lines.reduce((total, line) => total + Math.round(line.price * 100) * line.quantity * line.vat / 100, 0));
		},
		isFormValid() {
			const s = this.supplier;
			return !!s.supplier_name.trim()
				&& !!s.supplier_address.trim()
				&& !!s.supplier_city.trim()
				&& !!s.supplier_country_code
				&& !!s.supplier_vat.trim()
				&& !!this.reference.numero.trim() && this.reference.numero.trim().length <= 20
				&& !!this.reference.date && this.reference.date <= isoDate(new Date())
				&& this.lines.length > 0
				&& this.lines.every(line => !!line.description.trim() && isValidQuantity(line.quantity) && line.price > 0);
		},
	},
	methods: {
		reset() {
			Object.assign(this.$data, this.$options.data.call(this));
		},
		countryName(code) {
			const name = euCountriesFile[code] || nonEuCountriesFile[code];
			return name ? code + " - " + name : code;
		},
		setAddress({data}) {
			this.supplier.supplier_address = data.via || "";
			this.supplier.supplier_civic = data.civico || "";
			this.supplier.supplier_zip = data.zip || "";
			this.supplier.supplier_city = data.comune || data.provincia || "";
			if(data.country && data.country !== this.supplier.supplier_country_code) {
				this.selectCountry(data.country);
			}
		},
		selectCountry(code) {
			this.countriesOpen = false;
			if(code === "IT" || code === "OO") {
				this.showNotification("self-invoice-invalid-country");
				return;
			}
			this.supplier.supplier_country_code = code;
			if(this.isEuCountry) {
				if(this.supplier.supplier_vat === EXTRA_EU_VAT) {
					this.supplier.supplier_vat = "";
				}
			} else if(!this.supplier.supplier_vat.trim()) {
				this.supplier.supplier_vat = EXTRA_EU_VAT;
			}
		},
		emitSelfInvoice() {
			if(!this.isFormValid || this.emitting) {
				return;
			}
			this.emitting = true;
			API.init().emitManualSelfInvoice(this.userStore.currBusinessId, {
				...this.supplier,
				reference_numero: this.reference.numero.trim(),
				reference_date: this.reference.date,
				items: this.lines.map(line => ({
					description: line.description,
					quantity: line.quantity,
					price: Math.round(line.price * 100),
					vat: line.vat,
				})),
			}, (response) => {
				this.emitting = false;
				if(response && response.result === "OK") {
					this.showNotification("generic-success");
					this.$emit("emitted");
					this.$emit("toggling-popup", false);
				} else if(response && response.details === "self_invoice_emission_failed") {
					// Saved as failed: it is retried from the table
					this.showNotification("fiscal-document-emission-failed", [String(response.self_invoice?.error || "-")]);
					this.$emit("emitted");
					this.$emit("toggling-popup", false);
				} else if(response && INVALID_DOCUMENT_ERRORS.includes(response.details)) {
					this.showNotification("fiscal-document-invalid", [this.getString("fiscal_document_error_" + response.details)]);
				} else {
					this.showNotification("generic-fail");
				}
			});
		},
	},
	watch: {
		active(isActive) {
			if(isActive) {
				this.reset();
			} else {
				this.countriesOpen = false;
			}
		},
	},
}
</script>

<style scoped lang="scss">
.form-hint {
	color: var(--color-txt-2);
	margin: 5px 0 0;
	font-size: 0.85em;
}

.self-invoice-form {
	display: flex;
	flex-direction: column;
	gap: 10px;
	padding-bottom: 20px;

	.self-invoice-section-title {
		font-weight: 600;
		margin: 20px 0 0;

		&:not(:first-child) {
			margin-top: 15px;
			padding-top: 20px;
			border-top: 1px solid var(--color-border-2);
		}
	}

	.form-grid {
		display: grid;
		grid-template-columns: 1fr;
		gap: 10px;
	}

	.country-input :deep(input) {
		cursor: pointer;
	}

	&.wide .form-grid {
		grid-template-columns: 1fr 1fr;
		column-gap: 15px;
		align-items: start;
	}
}

.totals {
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: 4px;
	margin-top: 10px;

	p {
		color: var(--color-txt-2);

		span {
			display: inline-block;
			min-width: 90px;
			text-align: right;
			color: var(--color-txt-1);
		}
	}

	.total {
		font-weight: 600;
	}
}
</style>
